import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, ButtonBase, Divider, Link, Skeleton, Typography } from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import {
  ShoppingCart as ShoppingCartIcon,
  Diamond as DiamondIcon,
  HourglassTop as HourglassTopIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { fetchMyActivity, fetchMyActivitySalesTrend } from '../../services/myActivity.service';
import type { MyActivityResponse, MyActivitySalesTrendPoint } from '../../types/myActivity.types';
import { MySalesWeekChart } from './MySalesWeekChart';
import { useNavigationPermissions } from '../../hooks/useNavigationPermissions';
import { formatCurrency } from '../../utils/formatters/money';
import { SALES_ORDER_STATUS_CONFIG } from '../../utils/constants/salesStatus';
import { getInventoryStatsIconSx } from '../inventory/InventoryStats/InventoryStats.styles';

/** "Mi semana" (gráfica de 7 días) oculta a pedido del cliente; poner en true para volver a mostrarla. */
const SHOW_MY_WEEK_CHART = false;

const todayRange = (): { from: string; to: string } => {
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  const to = new Date();
  to.setHours(23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
};

/** Last 7 local days (today included), plus the browser timezone for day grouping. */
const lastSevenDaysRange = (): { from: string; to: string; tz: string } => {
  const from = new Date();
  from.setDate(from.getDate() - 6);
  from.setHours(0, 0, 0, 0);
  const to = new Date();
  to.setHours(23, 59, 59, 999);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  return { from: from.toISOString(), to: to.toISOString(), tz };
};

const formatSaleDate = (iso: string): string => {
  const date = new Date(iso);
  const isToday = date.toDateString() === new Date().toDateString();
  return isToday
    ? date.toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('es-BO', { day: 'numeric', month: 'short' });
};

/** Same surface as the pending bar (StatsContainer): white card, soft shadow, border only in dark. */
const cardSx = (t: Theme) => ({
  backgroundColor: t.palette.mode === 'dark' ? t.palette.background.paper : '#ffffff',
  borderRadius: '16px',
  boxShadow: t.palette.mode === 'dark' ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.08)',
  border: t.palette.mode === 'dark' ? `1px solid ${t.palette.divider}` : 'none',
  p: 2.5,
  display: 'flex',
  flexDirection: 'column' as const,
});

const circleIconSx = {
  width: 40,
  height: 40,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

type StatusColor = 'default' | 'info' | 'warning' | 'success' | 'error';

/** Soft status pill: pale background of the status color + colored dot. */
const StatusPill: React.FC<{ label: string; color: StatusColor }> = ({ label, color }) => {
  const theme = useTheme();
  const main = color === 'default' ? theme.palette.text.secondary : theme.palette[color].main;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        color: main,
        backgroundColor: alpha(main, theme.palette.mode === 'dark' ? 0.2 : 0.1),
      }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: main }} />
      {label}
    </Box>
  );
};

/**
 * Inicio "Mi actividad" (home.my-activity.view): today's counters, last 7 days chart and
 * last 5 sales of the logged-in user. Sales open their detail only if the role can view Gestión de ventas.
 */
export const MyActivityPanel: React.FC = () => {
  const theme = useTheme();
  const iconSx = getInventoryStatsIconSx(theme);
  const navigate = useNavigate();
  const { canSales } = useNavigationPermissions();
  const [data, setData] = useState<MyActivityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trend, setTrend] = useState<MyActivitySalesTrendPoint[]>([]);
  const [trendLoading, setTrendLoading] = useState(true);
  const [trendError, setTrendError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetchMyActivity(todayRange(), controller.signal)
      .then((res) => {
        setData(res);
        setError(null);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'No se pudo cargar la actividad.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!SHOW_MY_WEEK_CHART) return undefined;
    const controller = new AbortController();
    setTrendLoading(true);
    fetchMyActivitySalesTrend(lastSevenDaysRange(), controller.signal)
      .then((res) => {
        setTrend(res);
        setTrendError(null);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setTrendError(err instanceof Error ? err.message : 'No se pudo cargar la tendencia.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setTrendLoading(false);
      });
    return () => controller.abort();
  }, []);

  const pendingPayment = data?.today.pendingPayment ?? 0;
  const counters = [
    {
      key: 'sales',
      label: 'Ventas',
      value: data?.today.salesCount ?? 0,
      icon: <ShoppingCartIcon fontSize="small" />,
      iconSx: iconSx.sales,
    },
    {
      key: 'pieces',
      label: 'Piezas vendidas',
      value: data?.today.piecesSold ?? 0,
      icon: <DiamondIcon fontSize="small" />,
      iconSx: iconSx.inventory,
    },
    {
      key: 'pending',
      label: 'Por cobrar',
      value: pendingPayment,
      icon: <HourglassTopIcon fontSize="small" />,
      iconSx: iconSx.warning,
      highlight: pendingPayment > 0,
    },
  ];

  const openSale = (id: number) =>
    navigate('/dashboard/orders', { state: { openOrderDetails: id } });

  return (
    <Box
      sx={{
        display: 'grid',
        // Con gráfica — md: Mi día + gráfica arriba, últimas ventas abajo a lo ancho; lg: las tres en fila.
        // Sin gráfica — md y lg: Mi día + últimas ventas en fila.
        gridTemplateColumns: SHOW_MY_WEEK_CHART
          ? { xs: '1fr', md: '1fr 2fr', lg: 'minmax(200px, 0.8fr) 1.6fr 1.6fr' }
          : { xs: '1fr', md: 'minmax(200px, 0.8fr) 2fr' },
        gap: 2,
        mb: 2,
        alignItems: 'stretch',
      }}
    >
      {/* Mi día */}
      <Box sx={cardSx}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
          Mi día
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            flex: 1,
            justifyContent: 'center',
          }}
        >
          {counters.map((c) => (
            <Box key={c.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ ...circleIconSx, ...c.iconSx }}>{c.icon}</Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {c.label}
                </Typography>
                {loading ? (
                  <Skeleton variant="text" width={32} height={32} />
                ) : (
                  <Typography
                    sx={{
                      fontSize: 24,
                      fontWeight: 700,
                      lineHeight: 1.2,
                      color: c.highlight ? 'warning.main' : 'text.primary',
                    }}
                  >
                    {c.value}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Mi semana (últimos 7 días) */}
      {SHOW_MY_WEEK_CHART && (
        <Box sx={cardSx}>
          <MySalesWeekChart data={trend} loading={trendLoading} error={trendError} />
        </Box>
      )}

      {/* Ventas recientes */}
      <Box
        sx={(t) => ({
          ...cardSx(t),
          gridColumn: SHOW_MY_WEEK_CHART ? { md: '1 / -1', lg: 'auto' } : 'auto',
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Ventas recientes
          </Typography>
          {canSales && (
            <Link
              component="button"
              underline="hover"
              onClick={() => navigate('/dashboard/orders')}
              sx={{ fontSize: 13, fontWeight: 600 }}
            >
              Ver todas →
            </Link>
          )}
        </Box>

        {error != null ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : loading ? (
          [0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={48} sx={{ my: 0.75 }} />)
        ) : data == null || data.recentSales.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              py: 3,
              color: 'text.secondary',
            }}
          >
            <Box sx={{ ...circleIconSx, ...iconSx.inventory }}>
              <ReceiptLongIcon fontSize="small" />
            </Box>
            <Typography variant="body2">
              Aún no tiene ventas. Cuando registre una, aparecerá aquí.
            </Typography>
          </Box>
        ) : (
          data.recentSales.map((sale, index) => {
            const status =
              SALES_ORDER_STATUS_CONFIG[sale.status as keyof typeof SALES_ORDER_STATUS_CONFIG];
            const details = [
              sale.customerName ?? 'Sin cliente',
              `${sale.itemsCount} ${sale.itemsCount === 1 ? 'pieza' : 'piezas'}`,
              formatSaleDate(sale.saleDate),
            ].join(' · ');
            return (
              <React.Fragment key={sale.id}>
                {index > 0 && <Divider sx={{ mx: 1 }} />}
                <ButtonBase
                  disabled={!canSales}
                  onClick={() => openSale(sale.id)}
                  sx={(t) => ({
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    width: '100%',
                    textAlign: 'left',
                    px: 1,
                    py: 1.25,
                    borderRadius: '12px',
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                      backgroundColor: alpha(
                        t.palette.primary.main,
                        t.palette.mode === 'dark' ? 0.12 : 0.04,
                      ),
                    },
                  })}
                >
                  <Box sx={{ ...circleIconSx, ...iconSx.sales }}>
                    <ReceiptLongIcon fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {sale.orderCode}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                      {details}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }}>
                      {formatCurrency(sale.totalAmount)}
                    </Typography>
                    {status && <StatusPill label={status.label} color={status.color} />}
                  </Box>
                </ButtonBase>
              </React.Fragment>
            );
          })
        )}
      </Box>
    </Box>
  );
};
